import React, { useEffect, useState, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faArrowUpWideShort, faArrowDownWideShort, faSort } from "@fortawesome/free-solid-svg-icons";
import { formatDateToDDMMYYYY } from '../../utils/formatDate';

import '../../css/datatable.css';

// Hook debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const Datatable = React.memo((props) => {
    // Estado para saber si el mouse está sobre el input de filtro de columna
    const [inputHoverColIdx, setInputHoverColIdx] = useState(null);
  const {
    titletable,
    titleDisabled = false,
    apiUrl,
    requestDataFn,
    columns,
    rowIdField = "id",
    defaultSortBy,
    defaultSortDir,
    initialSortBy,
    initialSortDir,
    maxPerPage = 10000,
    initialPerPage = 10,
    orderColumns = true,
    checkboxColumn = false,
    rows: rowsProp = [],
    searchEnabled = true,
    searchColumnsEnabled = true,
    nameColumnsUppercase = true,
    adjustColumnSize = false,
    dataPagination,
    onPageChange,
    onPerPageChange,
    onSortChange,
    onColumnFilterChange,
    onGlobalSearchChange,
    manualPageInput = false,
    forceLoading = false,
    rowsPerPageOptions = [10, 20, 50, 100],
    onRowClick,
    onRowDoubleClick
  } = props;

  // --- CONFIGURACIÓN DE COLUMNAS ---
  const initialCols = useMemo(() => {
    return (columns || []).map(col => {
      if (col.searchForColumn !== undefined) {
        return { ...col, searchable: !!col.searchForColumn };
      }
      return col;
    });
  }, [columns]);

  const [cols, setCols] = useState(initialCols);
  
  // Inicializa colWidths. 
  const getInitialColWidths = colsArr => (colsArr || []).map(col => col.width ? col.width : (col.minWidth ? col.minWidth : '150px'));
  const [colWidths, setColWidths] = useState(() => getInitialColWidths(columns));

  useEffect(() => {
    setColWidths(getInitialColWidths(columns));
  }, [columns]);
  
  const [draggingKey, setDraggingKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);

  // --- ESTADO DE DATOS Y PAGINACIÓN ---
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadingState = forceLoading || isLoading;

  // Estados Internos
  const [internalPage, setInternalPage] = useState(1);
  const [internalPerPage, setInternalPerPage] = useState(initialPerPage);
  const [internalTotal, setInternalTotal] = useState(0);

  // Estados de Filtros y Orden
  const [sortBy, setSortBy] = useState(initialSortBy || '');
  const [sortDir, setSortDir] = useState(initialSortDir || '');
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  
  const debouncedGlobalSearch = useDebounce(globalSearch, 400);
  const debouncedColumnFilters = useDebounce(columnFilters, 400);
  const debouncedSortBy = useDebounce(sortBy, 400);
  const debouncedSortDir = useDebounce(sortDir, 400);
  const [manualInputPage, setManualInputPage] = useState();

  // --- LÓGICA HÍBRIDA ---
  const isControlled = !!dataPagination; 
  const effectivePage = isControlled ? dataPagination.current_page : internalPage;
  const effectivePerPage = isControlled ? dataPagination.per_page : internalPerPage;
  const effectiveTotal = isControlled ? dataPagination.total : internalTotal;
  const effectiveLastPage = dataPagination?.last_page ?? (Math.ceil(effectiveTotal / effectivePerPage) || 1);
  const totalPages = effectiveLastPage;

  // --- FETCH CENTRALIZADO ---
  const fetchIdRef = React.useRef(0);
  const fetchData = useCallback(async () => {
    if (!requestDataFn && !apiUrl) return;
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    try {
      const safePerPage = effectivePerPage > maxPerPage ? maxPerPage : effectivePerPage;
      const params = {
        page: effectivePage,
        perPage: safePerPage,
        sortBy: debouncedSortBy,
        sortDir: debouncedSortDir,
        search: debouncedGlobalSearch.trim(),
        filters: debouncedColumnFilters
      };
      let resultData = [];
      let resultTotal = 0;
      if (typeof requestDataFn === 'function') {
        const response = await requestDataFn(params);
        resultData = response.data || [];
        resultTotal = response.total || 0;
      } else if (apiUrl) {
        const urlParams = new URLSearchParams();
        urlParams.set("page", String(params.page));
        urlParams.set("per_page", String(params.perPage));
        if (params.sortBy && params.sortDir) {
          urlParams.set("sort_by", params.sortBy);
          urlParams.set("sort_dir", params.sortDir);
        }
        if (params.search) urlParams.set("search", params.search);
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value.trim()) urlParams.set(`filter[${key}]`, value.trim());
        });
        const resp = await fetch(`${apiUrl}?${urlParams.toString()}`);
        if (!resp.ok) throw new Error(`Error ${resp.status}`);
        const json = await resp.json();
        resultData = json.data || [];
        resultTotal = json.total || 0;
      }
      
      if (fetchIdRef.current === fetchId) {
        setRows(resultData);
        setInternalTotal(resultTotal);
        setIsLoading(false);
      }
    } catch (error) {
      if (fetchIdRef.current === fetchId) {
        setRows([]);
        setInternalTotal(0);
        setIsLoading(false);
      }
      console.error("Datatable fetch error:", error);
    }
  }, [apiUrl, requestDataFn, effectivePage, effectivePerPage, maxPerPage, debouncedSortBy, debouncedSortDir, debouncedGlobalSearch, debouncedColumnFilters]);

  useEffect(() => {
    if (requestDataFn || apiUrl) {
      fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    if (!apiUrl && !requestDataFn && Array.isArray(rowsProp)) {
      setRows(rowsProp);
      setInternalTotal(rowsProp.length);
    }
  }, [apiUrl, requestDataFn, rowsProp]);

  // --- HANDLERS ---
  const handleSortClick = (columnKey, direction) => {
    if (!columnKey) return;
    let newDirection = direction;
    if (!direction) {
      newDirection = (sortBy === columnKey && sortDir === "asc") ? "desc" : "asc";
    }
    setSortBy(columnKey);
    setSortDir(newDirection);
    if (typeof onSortChange === 'function') onSortChange(columnKey, newDirection);
  };

  const handleGlobalSearchChange = (e) => {
    setGlobalSearch(e.target.value);
    if (!isControlled) setInternalPage(1);
  };

  const handleGlobalSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
        if (typeof onGlobalSearchChange === 'function') onGlobalSearchChange(globalSearch);
    }
  };

  const handleColumnFilterChange = (columnKey, value) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
    if (!isControlled) setInternalPage(1);
    if (typeof onColumnFilterChange === 'function') onColumnFilterChange(columnKey, value);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    if (isControlled && typeof onPageChange === 'function') {
      onPageChange(nextPage);
    } else {
      setInternalPage(nextPage);
    }
  };

  const handlePerPageChange = (e) => {
    const value = Number(e.target.value) || 10;
    const safe = value > maxPerPage ? maxPerPage : value;
    
    setManualInputPage(undefined); 

    if (isControlled && typeof onPerPageChange === 'function') {
      onPerPageChange(safe);
      if (typeof onPageChange === 'function') onPageChange(1);
    } else {
      setInternalPerPage(safe);
      setInternalPage(1);
    }
  };

  // Drag & drop logic
  const handleDragStart = (key) => {
    if (orderColumns) setDraggingKey(key);
  };
  const handleDragOver = (e, key, colIdx) => {
    if (!orderColumns || !draggingKey || draggingKey === key) return;
    e.preventDefault();
    setDragOverKey(key);
    setDragOverColIdx(colIdx);
  };
  const handleDragLeave = (e, key) => {
    if (!orderColumns) return;
    setDragOverKey(null);
    setDragOverColIdx(null);
  };
  const handleDrop = (e, targetKey) => {
    if (!orderColumns || !draggingKey) return;
    e.preventDefault();
    if (draggingKey !== targetKey) {
      const newCols = [...cols];
      const newColWidths = [...colWidths];
      const fromIdx = newCols.findIndex(c => c.key === draggingKey);
      const toIdx = newCols.findIndex(c => c.key === targetKey);
      if (fromIdx !== -1 && toIdx !== -1) {
        const [movedCol] = newCols.splice(fromIdx, 1);
        newCols.splice(toIdx, 0, movedCol);
        const [movedWidth] = newColWidths.splice(fromIdx, 1);
        newColWidths.splice(toIdx, 0, movedWidth);
        setCols(newCols);
        setColWidths(newColWidths);
      }
    }
    setDraggingKey(null);
    setDragOverKey(null);
    setDragOverColIdx(null);
  };

  // ------------------ RENDERIZADO ------------------
  return (
    <div className="d-flex flex-column gap-3 w-100" style={{  background: 'rgb(42 119 185)', borderRadius: 12, boxShadow: '0 2px 8px rgba(30,70,130,0.10)' }}>
      
      {/* Título y Buscador */}
      <div className="d-flex justify-content-between align-items-center w-100 mt-2">
        <div className="d-flex align-items-center ms-2">
          {!titleDisabled && titletable && (
            <h5 className="mb-0 text-white" style={{ letterSpacing: 1 }}>{titletable}</h5>
          )}
        </div>
        {searchEnabled && (
          <div className="d-flex align-items-center gap-2 me-2" style={{ background: '#14406e', borderRadius: 8, padding: '4px 12px' }}>
            <span className="small fw-semibold text-white">Buscar:</span>
            <input
              type="text"
              value={globalSearch}
              onChange={handleGlobalSearchChange}
              onKeyDown={handleGlobalSearchKeyDown}
              className="form-control"
              placeholder="Buscar en todas las columnas..."
              style={{ minWidth: 220, borderRadius: 8, border: '1px solid #14406e', background: '#dbe7f6', color: '#14406e', fontSize: 15, height: 36, padding: '6px 12px' }}
              draggable={false}
              onDragStart={e => e.preventDefault()}
            />
          </div>
        )}
      </div>

      {/* Contenedor de la Tabla */}
      <div
        className="table-responsive border rounded shadow-sm datatable-scrollbar"
        style={{ maxHeight: 600, overflow: "auto", background: '#fff', border: '1px solid #14406e22', borderRadius: 10 }}
      >
        <table 
          className="table table-sm table-hover mb-0"
          style={{ 
            tableLayout: adjustColumnSize ? 'fixed' : 'auto',
            width: adjustColumnSize ? 'max-content' : '100%',
            minWidth: '100%'
          }}
        >
          <thead className="table-light sticky-top" style={{ top: 0, zIndex: 5 }}>
            {/* Fila de nombres de columnas */}
            <tr style={{ background: '#dbe7f6' }}>
              {checkboxColumn && (
                <th style={{ width: 40 }} rowSpan={searchColumnsEnabled ? 2 : 1}>
                  <input type="checkbox" disabled />
                </th>
              )}
              {cols.map((col, colIdx) => {
                const isSorted = sortBy === col.key;
                const dir = isSorted ? sortDir : null;
                const headerAlign = col.headerAlign || 'left';
                const label = nameColumnsUppercase ? String(col.label).toUpperCase() : col.label;
                
                // MODIFICADO: Usar el ancho definido siempre, sin depender de adjustColumnSize
                const currentWidth = colWidths[colIdx]
                  ? colWidths[colIdx]
                  : (col.minWidth ? col.minWidth : 'auto');

                // Drag & drop visual styles
                let dragStyle = {};
                if (draggingKey === col.key) {
                  dragStyle = {
                    background: 'rgba(42,119,185,0.18)',
                    opacity: 0.7,
                    border: '2px dashed #2a77b9',
                    zIndex: 20,
                  };
                } else if (dragOverKey === col.key && draggingKey) {
                  dragStyle = {
                    background: 'rgba(42,119,185,0.38)',
                    borderLeft: '48px solid #1976d2',
                    boxShadow: '0 0 0 32px #1976d244',
                    position: 'relative',
                    transition: 'box-shadow 0.2s, background 0.2s, border 0.2s',
                  };
                }

                const thStyle = {
                  textAlign: headerAlign,
                  width: currentWidth,
                  minWidth: col.minWidth || (adjustColumnSize ? '50px' : 'auto'),
                  position: "relative",
                  borderRight: colIdx < cols.length - 1 ? "1px solid #dee2e6" : undefined,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  transition: 'background 0.2s, box-shadow 0.2s, border 0.2s',
                  ...dragStyle,
                };

                const handleResizerMouseDown = (e) => {
                  if (!adjustColumnSize) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const headerCell = e.target.closest('th');
                  if (!headerCell) return;
                  const startX = e.clientX;
                  const startWidth = headerCell.getBoundingClientRect().width;
                  const onMouseMove = (ev) => {
                    requestAnimationFrame(() => {
                      const newWidth = Math.max(50, startWidth + (ev.clientX - startX));
                      setColWidths(prev => {
                        const updated = [...prev];
                        updated[colIdx] = newWidth + "px";
                        return updated;
                      });
                    });
                  };
                  const onMouseUp = () => {
                    document.removeEventListener("mousemove", onMouseMove);
                    document.removeEventListener("mouseup", onMouseUp);
                  };
                  document.addEventListener("mousemove", onMouseMove);
                  document.addEventListener("mouseup", onMouseUp);
                };

                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={thStyle}
                    className="datatable-table-th"
                    draggable={orderColumns}
                    onDragStart={(e) => {
                      if (e.target.classList.contains('th-resize-handle')) {
                        e.preventDefault();
                        return false;
                      }
                        if (e.target.tagName === 'INPUT') {
                          e.preventDefault();
                          return false;
                        }
                        handleDragStart(col.key);
                    }}
                    onDragOver={(e) => handleDragOver(e, col.key, colIdx)}
                    onDragLeave={(e) => handleDragLeave(e, col.key)}
                    onDrop={(e) => handleDrop(e, col.key)}
                    onDragEnd={() => {
                      setDraggingKey(null);
                      setDragOverKey(null);
                      setDragOverColIdx(null);
                    }}
                  >
                    <div className="d-flex align-items-center gap-1 justify-content-between">
                      <span className="small fw-semibold text-truncate" title={label}>{label}</span>
                      {col.sortable && (
                        <div className="th-sort">
                          {isSorted ? (
                            dir === 'asc' ? (
                              <button
                                type="button"
                                onClick={() => handleSortClick(col.key, 'desc')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  marginRight: 4,
                                  fontSize: 13,
                                  lineHeight: 1.2,
                                  color: '#1976d2',
                                  cursor: 'pointer',
                                }}
                                title="Cambiar a descendente"
                              >
                                <FontAwesomeIcon icon={faArrowUpWideShort} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleSortClick(col.key, 'asc')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  marginLeft: 2,
                                  fontSize: 13,
                                  lineHeight: 1.2,
                                  color: '#1976d2',
                                  cursor: 'pointer',
                                }}
                                title="Cambiar a ascendente"
                              >
                                <FontAwesomeIcon icon={faArrowDownWideShort} />
                              </button>
                            )
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSortClick(col.key, 'asc')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  marginRight: 4,
                                  fontSize: 13,
                                  lineHeight: 1.2,
                                  color: '#b0b0b0',
                                  cursor: 'pointer',
                                }}
                                title="Orden ascendente"
                              >
                                <FontAwesomeIcon icon={faArrowUpWideShort} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSortClick(col.key, 'desc')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  marginLeft: 2,
                                  fontSize: 13,
                                  lineHeight: 1.2,
                                  color: '#b0b0b0',
                                  cursor: 'pointer',
                                }}
                                title="Orden descendente"
                              >
                                <FontAwesomeIcon icon={faArrowDownWideShort} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {adjustColumnSize && (
                      <div
                        className="th-resize-handle"
                        onMouseDown={handleResizerMouseDown}
                        onClick={e => e.stopPropagation()}
                        title="Arrastra para cambiar ancho"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
            {/* Fila de buscadores por columna */}
            {searchColumnsEnabled && (
              <tr style={{ background: '#eaf2fb' }}>
                {checkboxColumn && <th style={{ width: 40 }}></th>}
                {cols.map((col, colIdx) => (
                  (() => {
                    // Drag & drop visual styles para buscador
                    let dragStyle = {};
                    if (draggingKey === col.key) {
                      dragStyle = {
                        background: 'rgba(42,119,185,0.18)',
                        opacity: 0.7,
                        border: '2px dashed #2a77b9',
                        zIndex: 20,
                      };
                    } else if (dragOverKey === col.key && draggingKey) {
                      dragStyle = {
                        background: 'rgba(42,119,185,0.38)',
                        borderLeft: '48px solid #1976d2',
                        boxShadow: '0 0 0 32px #1976d244',
                        position: 'relative',
                        transition: 'box-shadow 0.2s, background 0.2s, border 0.2s',
                      };
                    }
                    // MODIFICADO: Usar el ancho siempre en los filtros también
                    const currentWidth = colWidths[colIdx]
                      ? colWidths[colIdx]
                      : (col.minWidth ? col.minWidth : 'auto');

                    const thStyle = {
                      padding: '2px',
                      width: currentWidth,
                      minWidth: col.minWidth || (adjustColumnSize ? '50px' : 'auto'),
                      borderRight: colIdx < cols.length - 1 ? "1px solid #dee2e6" : undefined,
                      boxSizing: 'border-box',
                      ...dragStyle,
                    };
                    return (
                      <th
                        key={col.key}
                        style={thStyle}
                        className="datatable-table-th"
                        draggable={orderColumns && inputHoverColIdx !== colIdx}
                        onDragStart={(e) => {
                          if (e.target.classList.contains('th-resize-handle') || e.target.tagName === 'INPUT') {
                            e.preventDefault();
                            return false;
                          }
                          handleDragStart(col.key);
                        }}
                        onDragOver={(e) => handleDragOver(e, col.key, colIdx)}
                        onDragLeave={(e) => handleDragLeave(e, col.key)}
                        onDrop={(e) => handleDrop(e, col.key)}
                        onDragEnd={() => {
                          setDraggingKey(null);
                          setDragOverKey(null);
                          setDragOverColIdx(null);
                        }}
                      >
                        <div className="mt-0">
                          <input
                            type="text"
                            value={columnFilters[col.key] || ""}
                            onChange={(e) => col.searchable ? handleColumnFilterChange(col.key, e.target.value) : undefined}
                            className={`form-control datatable-search-input${col.searchable ? '' : ' disabled'}`}
                            placeholder={col.searchable ? `Filtrar por ` : "------"}
                            style={{ width: '100%', background: col.searchable ? undefined : '#f4f4f4', color: col.searchable ? undefined : '#999', fontSize: 14, height: 32, padding: '4px 4px', borderRadius: 6, border: '1px solid #bcd4ec' }}
                            disabled={!col.searchable}
                            draggable={false}
                            onDragStart={e => e.preventDefault()}
                            onMouseEnter={() => setInputHoverColIdx(colIdx)}
                            onMouseLeave={() => setInputHoverColIdx(null)}
                          />
                        </div>
                      </th>
                    );
                  })()
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {loadingState ? (
              Array.from({ length: effectivePerPage }).map((_, rowIdx) => (
                <tr key={`skeleton-row-${rowIdx}`}>
                  {checkboxColumn && (
                    <td style={{ width: 40 }}><input type="checkbox" disabled /></td>
                  )}
                  {cols.map((col, colIdx) => {
                    const dataAlign = col.dataAlign || 'left';
                    const cellStyle = {
                      textAlign: dataAlign,
                      // MODIFICADO: Aplicar anchos al esqueleto también
                      width: colWidths[colIdx] || undefined,
                      maxWidth: colWidths[colIdx] || undefined,
                      minWidth: col.minWidth || undefined,
                      borderRight: colIdx < cols.length - 1 ? "1px solid #14406e22" : undefined,
                      color: '#222', fontSize: 13, padding: '8px 8px', boxSizing: 'border-box',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      transition: 'background 0.2s, box-shadow 0.2s, border 0.2s',
                    };
                    if (draggingKey === col.key) {
                      cellStyle.background = 'rgba(42,119,185,0.18)';
                      cellStyle.opacity = 0.7;
                      cellStyle.border = '2px dashed #2a77b9';
                      cellStyle.zIndex = 10;
                    } else if (dragOverColIdx === colIdx && draggingKey) {
                      cellStyle.background = 'rgba(42,119,185,0.38)';
                      cellStyle.borderLeft = '48px solid #1976d2';
                      cellStyle.boxShadow = '0 0 0 32px #1976d244';
                      cellStyle.position = 'relative';
                    }
                    return (
                      <td key={`skeleton-cell-${col.key}-${rowIdx}`} style={cellStyle}>
                        <div className="skeleton-box wave" style={{ height: 18, width: '100%', borderRadius: 6, margin: 0 }} />
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              rows.length === 0 ? (
                <tr>
                  <td colSpan={cols.length + (checkboxColumn ? 1 : 0)} className="text-center py-3" style={{ background: '#e6eef5', color: '#14406e', fontWeight: 500 }}>
                    No se encontraron registros.
                  </td>
                </tr>
              ) : (
                rows.slice(0, effectivePerPage).map((row, idx) => (
                  <DatatableRow
                    key={row[rowIdField] ?? `${rowIdField}-${idx}`}
                    row={row}
                    idx={idx}
                    cols={cols}
                    colWidths={colWidths}
                    checkboxColumn={checkboxColumn}
                    adjustColumnSize={adjustColumnSize}
                    rowIdField={rowIdField}
                    onRowClick={onRowClick}
                    onRowDoubleClick={onRowDoubleClick}
                    draggingKey={draggingKey}
                    dragOverColIdx={dragOverColIdx}
                  />
                ))
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Paginador y selectores */}
      <div className="d-flex justify-content-end align-items-center w-100 mb-3">
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#dbe7f6', borderRadius: '12px', boxShadow: '0 1px 4px rgba(20,64,110,0.07)',
          border: '1px solid #14406e22', padding: '8px 24px', minWidth: 600, gap: '32px',
        }} className="me-3">
          
          <div className="d-flex align-items-center gap-2 small " style={{ background: 'transparent', borderRadius: 8, padding: '0 10px' }}>
            <span style={{ color: '#14406e', fontWeight: 500 }}>Filas por página:</span>
            <select
              value={effectivePerPage}
              onChange={handlePerPageChange}
              className="form-select form-select-sm"
              style={{ borderRadius: 6, border: '1px solid #14406e33', background: '#e6eef5', color: '#14406e', fontWeight: 500, width: 120, minWidth: 100 }}
            >
              {rowsPerPageOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          
          <div className="text-muted small" style={{ color: '#14406e', fontWeight: 500, fontSize: 15 }}>
            Mostrando <span style={{ color: '#14406e', fontWeight: 700 }}>{rows.length > 0 ? (effectivePage - 1) * effectivePerPage + 1 : 0}</span>–
            <span style={{ color: '#14406e', fontWeight: 700 }}>{Math.min(effectivePage * effectivePerPage, effectiveTotal)}</span> de <span style={{ color: '#14406e', fontWeight: 700 }}>{effectiveTotal}</span> registros
          </div>
          
          <div className="d-flex align-items-center gap-1 small" style={{ background: 'transparent', borderRadius: 8, padding: '0 10px', borderLeft: '1px solid #14406e22', marginLeft: 12 }}>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handlePageChange(1)} disabled={effectivePage === 1} style={{ borderRadius: 6, border: '1px solid #14406e', color: '#14406e', background: '#fff', fontWeight: 600 }} title="Ir al inicio">{'|<'}</button>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handlePageChange(effectivePage - 1)} disabled={effectivePage <= 1} style={{ borderRadius: 6, border: '1px solid #14406e', color: '#14406e', background: '#fff', fontWeight: 600 }} title="Página anterior">{'<'}</button>
            
            <span style={{ color: '#14406e', fontWeight: 600 }}>
              {manualPageInput ? (
                <>
                  Página <input type="text" inputMode="numeric" pattern="[0-9]*"
                    value={manualInputPage !== undefined ? manualInputPage : effectivePage}
                    onChange={e => setManualInputPage(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const val = Number(manualInputPage);
                        if (val >= 1 && val <= totalPages) handlePageChange(val);
                        else setManualInputPage(effectivePage);
                      }
                    }}
                    style={{ width: 60, textAlign: 'center', borderRadius: 6, border: '1px solid #14406e33', marginRight: 4 }}
                  /> de {totalPages}
                </>
              ) : (
                <>Página {effectivePage} de {totalPages}</>
              )}
            </span>

            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handlePageChange(effectivePage + 1)} disabled={effectivePage >= totalPages} style={{ borderRadius: 6, border: '1px solid #14406e', color: '#14406e', background: '#fff', fontWeight: 600 }} title="Página siguiente">{'>'}</button>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handlePageChange(totalPages)} disabled={effectivePage === totalPages} style={{ borderRadius: 6, border: '1px solid #14406e', color: '#14406e', background: '#fff', fontWeight: 600 }} title="Ir al final">{'>|'}</button>
          </div>
        </div>
      </div>
    </div>
  );
});

// Componente de Fila Memoizado
const DatatableRow = React.memo(({ row, idx, cols, colWidths, checkboxColumn, adjustColumnSize, rowIdField, onRowClick, onRowDoubleClick, draggingKey, dragOverColIdx }) => {
  return (
    <tr
      key={row[rowIdField] ?? `${rowIdField}-${idx}`}
      style={{ cursor: (onRowClick || onRowDoubleClick) ? 'pointer' : undefined, background: idx % 2 === 0 ? '#fff' : '#e6eef5', transition: 'background 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#dbe7f6'}
      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#e6eef5'}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
    >
      {checkboxColumn && <td><input type="checkbox" /></td>}
      {cols.map((col, colIdx) => {
        let value = col.selector ? col.selector(row) : row[col.key];
        if (col.type === 'date' && value) value = formatDateToDDMMYYYY(value);
        const dataAlign = col.dataAlign || 'left';
        
        // --- MODIFICADO: Estilos de celda para forzar el width y ellipsis ---
        let tdStyle = {
          textAlign: dataAlign,
          // Se aplica el ancho definido independientemente de adjustColumnSize
          width: colWidths[colIdx] || undefined, 
          // Se agrega maxWidth para forzar que el texto no expanda la celda, creando los "..."
          maxWidth: colWidths[colIdx] || undefined,
          minWidth: col.minWidth || undefined,
          
          borderRight: colIdx < cols.length - 1 ? "1px solid #14406e22" : undefined,
          color: '#222', fontSize: 13, padding: '7px 8px', boxSizing: 'border-box',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          transition: 'background 0.2s, box-shadow 0.2s, border 0.2s',
        };
        // -------------------------------------------------------------------

        if (draggingKey === col.key) {
          tdStyle = {
            ...tdStyle,
            background: 'rgba(42,119,185,0.18)',
            opacity: 0.7,
            border: '2px dashed #2a77b9',
            zIndex: 10,
          };
        } else if (dragOverColIdx === colIdx && draggingKey) {
          tdStyle = {
            ...tdStyle,
            background: 'rgba(42,119,185,0.38)',
            borderLeft: '48px solid #1976d2',
            boxShadow: '0 0 0 32px #1976d244',
            position: 'relative',
            transition: 'box-shadow 0.2s, background 0.2s, border 0.2s',
          };
        }
        return (
          <td key={col.key} style={tdStyle} title={typeof value === 'string' ? value : ''}>
            {col.render ? col.render(value, row) : value ?? ""}
          </td>
        );
      })}
    </tr>
  );
});

export default Datatable;